import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PathLink from "../../shared/seo/PathLink";
import { urls, currentLang } from "../../shared/seo/links";
import {
  COURTS,
  hasProfilePhoto,
  initials,
  lawyerAddress,
  lawyerGovernorate,
  lawyerName,
  lawyerProfileUrl,
  lawyerState,
  takesOnlineBooking,
} from "./lawyerHelpers";
import "./lawyers.css";

export default function LawyerCard(props) {
  const Lng = currentLang();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lawyer = props.lawyer;
  const name = lawyerName(lawyer, Lng);
  const profile = lawyerProfileUrl(lawyer, Lng);
  const governorate = lawyerGovernorate(lawyer);
  const level = urls.findBarLevel(lawyer.barLevel);
  // A broken photo URL falls back to the initials placeholder.
  const [photoFailed, setPhotoFailed] = useState(false);
  const hasPhoto = hasProfilePhoto(lawyer) && !photoFailed;

  function BookAppointement() {
    if (takesOnlineBooking(lawyer)) {
      navigate("/book-appointment/" + lawyer.id);
    } else {
      navigate(profile);
    }
  }

  // The directory only returns public lawyers and omits "verified"; older
  // endpoints send it, and unverified accounts stay hidden.
  return lawyer.verified === false ? null : (
    <article className="avc-lawyer-card">
      <PathLink
        to={profile}
        className="avc-lawyer-card__avatar"
        aria-label={name}
        tabIndex={-1}
      >
        {hasPhoto ? (
          <img
            src={lawyer.profilePicture}
            alt=""
            loading="lazy"
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <span aria-hidden="true">{initials(name)}</span>
        )}
      </PathLink>
      <div className="avc-lawyer-card__body">
        <h3 className="avc-lawyer-card__name">
          <PathLink to={profile}>{name}</PathLink>
        </h3>
        {/* Next to the name, not in it: the heading stays the lawyer's name.
            A fact a client can use ("books online"), never a paid quality
            mark: lawyers may not advertise, nor be ranked by what they pay. */}
        {lawyer.subscribed && (
          <span className="avc-booking-tag avc-booking-tag--card">
            <i className="far fa-calendar-check" aria-hidden="true"></i> {t("booking_tag")}
          </span>
        )}
        {level ? (
          <span className="avc-tag">{level[Lng]}</span>
        ) : (
          COURTS.includes(lawyer.speciality) && (
            <span className="avc-tag">{t(lawyer.speciality)}</span>
          )
        )}
        {governorate ? (
          <PathLink
            to={urls.lawyersPath(Lng, { governorate })}
            className="avc-lawyer-card__location"
          >
            <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
            <span>
              <strong>{lawyerState(lawyer, Lng)}</strong>
              {lawyerAddress(lawyer, Lng) ? " · " + lawyerAddress(lawyer, Lng) : ""}
            </span>
          </PathLink>
        ) : (
          <span className="avc-lawyer-card__location">
            <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
            <span>
              <strong>{lawyerState(lawyer, Lng)}</strong>
              {lawyerAddress(lawyer, Lng) ? " · " + lawyerAddress(lawyer, Lng) : ""}
            </span>
          </span>
        )}
      </div>
      <div className="avc-lawyer-card__actions">
        <PathLink to={profile} className="avc-btn avc-btn--outline">
          {t("View_Profile")}
        </PathLink>
        {lawyer.source !== "onat" && (
          <button
            type="button"
            className="avc-btn avc-btn--gold"
            onClick={BookAppointement}
          >
            <i className="far fa-calendar-check" aria-hidden="true"></i>
            {t("Book_Appointment")}
          </button>
        )}
      </div>
    </article>
  );
}
